import React, { useState, useEffect, useRef } from "react";
import { 
    BsSearch, 
    BsArrowLeft, 
    BsPeople, 
    BsGeoAlt, 
    BsGraphUp, 
    BsCalendar,
    BsEye,
    BsPencilSquare,
    BsCodeSlash,
    BsClockHistory,
    BsChevronDown,
    BsChevronUp
} from "react-icons/bs";
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Modal from 'react-bootstrap/Modal';

import '../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import useFetch from "../common/hooks/useFetch";
import { useNavigate } from "react-router-dom";

// Helper to format Date to YYYY-MM-DD
const toISODateString = (date) => {
    return date.toISOString().split('T')[0];
};

// Date calculation helpers
const get30DaysAgoRange = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return { start: toISODateString(thirtyDaysAgo), end: toISODateString(today) };
};

const getThisMonthRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: toISODateString(firstDay), end: toISODateString(today) };
};

const getLastMonthRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: toISODateString(firstDay), end: toISODateString(lastDay) };
};

// Action path translator
const translateAction = (method, path) => {
    if (!path) return "Ação desconhecida";
    const cleanPath = path.split('?')[0].replace(/\/+$/, ''); // Remove query params and trailing slash

    if (cleanPath === "/login") return "Efetuou login no sistema";
    if (cleanPath === "/logout") return "Efetuou logout";

    // User administration
    if (cleanPath === "/admin/users") return "Visualizou lista de usuários";
    if (cleanPath === "/admin/user/add") return "Criou um novo usuário";
    if (cleanPath.startsWith("/admin/user/edit/")) return "Editou dados de um usuário";
    if (cleanPath.startsWith("/admin/user/") && method === "DELETE") return "Excluiu um usuário";
    if (cleanPath.startsWith("/admin/user/")) return "Visualizou detalhes de usuário";

    // City administration
    if (cleanPath === "/admin/cities") return "Visualizou lista de cidades";
    if (cleanPath === "/admin/city/add") return "Adicionou nova cidade";
    if (cleanPath.startsWith("/admin/city/edit/")) return "Editou dados/camadas de cidade";
    if (cleanPath.startsWith("/admin/city/jsons/")) return "Carregou camadas da cidade";
    if (cleanPath.startsWith("/admin/city/dashboard")) return "Acessou painel administrativo da cidade";
    if (cleanPath.startsWith("/admin/city/") && method === "DELETE") return "Excluiu uma cidade";
    if (cleanPath.startsWith("/admin/city/")) return "Visualizou dados de uma cidade";

    // Data/Map access
    if (cleanPath.startsWith("/igu/")) return "Visualizou informações no mapa";
    if (cleanPath.startsWith("/od/")) return "Acessou dados de pesquisa Origem-Destino (OD)";

    return `${method} ${path}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
        case 'admin':
            return 'bg-danger';
        case 'editor':
            return 'bg-primary';
        case 'colab':
            return 'bg-success';
        default:
            return 'bg-secondary';
    }
};

const getMethodBadgeClass = (method) => {
    switch (method?.toUpperCase()) {
        case 'GET':
            return 'bg-info text-dark';
        case 'POST':
            return 'bg-success';
        case 'PUT':
            return 'bg-warning text-dark';
        case 'DELETE':
            return 'bg-danger';
        default:
            return 'bg-secondary';
    }
};

// Generates dynamic natural language description for staff actions
const getFriendlySummaryText = (cityStats) => {
    const parts = [];
    if (cityStats.edits > 0) {
        parts.push(`${cityStats.edits} ${cityStats.edits === 1 ? 'alteração' : 'alterações'}`);
    }
    if (cityStats.views > 0) {
        parts.push(`${cityStats.views} ${cityStats.views === 1 ? 'visualização' : 'visualizações'}`);
    }
    
    const actionText = parts.length === 2 
        ? `${parts[0]} e ${parts[1]}` 
        : parts[0] || "nenhuma atividade";
        
    const displayCity = cityStats.cityName.toLowerCase() === "all" ? "Todas as Cidades" : cityStats.cityName;
    return `Fez ${actionText} na cidade de ${displayCity}.`;
};

const LogsPage = () => {
    const navigate = useNavigate();

    // Active Tab state
    const [activeTab, setActiveTab] = useState("cities-clients");

    // Modal details states
    const [selectedCityGroup, setSelectedCityGroup] = useState(null);
    const [selectedClientDetail, setSelectedClientDetail] = useState(null);
    const [showTechLogs, setShowTechLogs] = useState(false);

    // Filters state
    const [periodType, setPeriodType] = useState("30days");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [queryUrl, setQueryUrl] = useState("/admin/logs");
    
    // Text search filter
    const [searchText, setSearchText] = useState("");

    // Collapsible states
    const [expandedStaff, setExpandedStaff] = useState({});

    // Fetch cities list to map IDs to Names
    const isCitiesMounted = useRef(true);
    const { data: citiesData } = useFetch("/admin/cities", isCitiesMounted, []);

    // Create a mapping of City ID -> City Name
    const cityMap = {};
    if (citiesData) {
        citiesData.forEach(c => {
            cityMap[c._id] = c.name;
        });
    }

    // Function to extract city name from path or log
    const getLogCityName = (log) => {
        if (!log.path) return "Geral";
        
        // 1. Check for 24-character hex ID in path
        const hex24Regex = /[0-9a-fA-F]{24}/;
        const match = log.path.match(hex24Regex);
        if (match) {
            const cityId = match[0];
            if (cityMap[cityId]) return cityMap[cityId];
        }

        // 2. Check if path contains any city name
        if (citiesData) {
            for (const city of citiesData) {
                if (log.path.toLowerCase().includes(city.name.toLowerCase())) {
                    return city.name;
                }
            }
        }

        // 3. Fallback to user's first allowed city if singular
        if (log.city && !log.city.includes(',')) {
            const name = log.city;
            return name.toLowerCase() === "all" ? "Todas as Cidades" : name;
        }

        return "Geral";
    };

    // Update query URL based on period selection
    useEffect(() => {
        let start = "";
        let end = "";

        if (periodType === "30days") {
            const range = get30DaysAgoRange();
            start = range.start;
            end = range.end;
        } else if (periodType === "thisMonth") {
            const range = getThisMonthRange();
            start = range.start;
            end = range.end;
        } else if (periodType === "lastMonth") {
            const range = getLastMonthRange();
            start = range.start;
            end = range.end;
        } else if (periodType === "custom") {
            start = customStart;
            end = customEnd;
        }

        if (start && end) {
            setQueryUrl(`/admin/logs?startDate=${start}&endDate=${end}`);
        } else {
            setQueryUrl("/admin/logs");
        }
    }, [periodType, customStart, customEnd]);

    // Fetch logs using the custom hook
    const isComponentMounted = useRef(true);
    const { data, loading, error } = useFetch(queryUrl, isComponentMounted, null);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        if (data) {
            setLogs(data);
        }
        return () => {
            isComponentMounted.current = false;
        };
    }, [data]);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value.toLowerCase());
    };

    const toggleStaff = (user) => {
        setExpandedStaff(prev => ({ ...prev, [user]: !prev[user] }));
    };

    // --- DATA AGGREGATION ---

    const cityClientsMap = {};
    const staffMap = {};

    logs.forEach(log => {
        const role = log.role?.toLowerCase() || 'user';
        const rawCityName = getLogCityName(log);
        const cityName = rawCityName.toLowerCase() === "all" ? "Todas as Cidades" : rawCityName;
        const userName = log.name || "Desconhecido";

        if (role === 'user') {
            // Clients aggregation
            if (!cityClientsMap[cityName]) {
                cityClientsMap[cityName] = {
                    cityName,
                    totalActions: 0,
                    lastAccessDate: "",
                    lastAccessTime: "",
                    lastAccessUser: "",
                    clients: {}
                };
            }
            const cityGroup = cityClientsMap[cityName];
            cityGroup.totalActions += 1;

            // Update latest client access in this city
            const logDateTime = `${log.date}T${log.time || "00:00:00"}`;
            const currentCityDateTime = `${cityGroup.lastAccessDate}T${cityGroup.lastAccessTime || "00:00:00"}`;
            if (!cityGroup.lastAccessDate || logDateTime > currentCityDateTime) {
                cityGroup.lastAccessDate = log.date;
                cityGroup.lastAccessTime = log.time;
                cityGroup.lastAccessUser = userName;
            }

            // Client details
            if (!cityGroup.clients[userName]) {
                cityGroup.clients[userName] = {
                    name: userName,
                    totalActions: 0,
                    actions: []
                };
            }
            const clientDetail = cityGroup.clients[userName];
            clientDetail.totalActions += 1;
            clientDetail.actions.push(log);

        } else {
            // Staff aggregation (admin, editor, colab)
            if (!staffMap[userName]) {
                staffMap[userName] = {
                    name: userName,
                    role: log.role,
                    totalActions: 0,
                    lastAccessDate: "",
                    lastAccessTime: "",
                    citiesWorked: {} // Keyed by city name
                };
            }
            const staffGroup = staffMap[userName];
            staffGroup.totalActions += 1;

            if (!staffGroup.citiesWorked[cityName]) {
                staffGroup.citiesWorked[cityName] = {
                    cityName,
                    views: 0,
                    edits: 0
                };
            }
            const cityStats = staffGroup.citiesWorked[cityName];
            if (log.method?.toUpperCase() === "GET") {
                cityStats.views += 1;
            } else {
                cityStats.edits += 1;
            }

            // Latest staff access
            const logDateTime = `${log.date}T${log.time || "00:00:00"}`;
            const currentStaffDateTime = `${staffGroup.lastAccessDate}T${staffGroup.lastAccessTime || "00:00:00"}`;
            if (!staffGroup.lastAccessDate || logDateTime > currentStaffDateTime) {
                staffGroup.lastAccessDate = log.date;
                staffGroup.lastAccessTime = log.time;
            }
        }
    });

    // Filtering logic for search
    const filteredCities = Object.values(cityClientsMap).filter(cityGroup => {
        const matchCity = cityGroup.cityName.toLowerCase().includes(searchText);
        const matchClientName = Object.keys(cityGroup.clients).some(c => c.toLowerCase().includes(searchText));
        return matchCity || matchClientName;
    }).sort((a, b) => b.totalActions - a.totalActions);

    const filteredStaff = Object.values(staffMap).filter(staffGroup => {
        const matchName = staffGroup.name.toLowerCase().includes(searchText);
        const matchRole = staffGroup.role?.toLowerCase().includes(searchText);
        const matchCities = Object.keys(staffGroup.citiesWorked).some(c => c.toLowerCase().includes(searchText));
        return matchName || matchRole || matchCities;
    }).sort((a, b) => b.totalActions - a.totalActions);

    // Filtered Logs list for detailed tab
    const filteredLogs = logs.filter(log => {
        const readableAction = translateAction(log.method, log.path).toLowerCase();
        return (
            (log.name?.toLowerCase() || "").includes(searchText) ||
            getLogCityName(log).toLowerCase().includes(searchText) ||
            (log.date?.toLowerCase() || "").includes(searchText) ||
            (log.role?.toLowerCase() || "").includes(searchText) ||
            readableAction.includes(searchText)
        );
    });

    // Overall metrics
    const totalActions = logs.length;
    const activeClientsCount = Object.keys(logs.reduce((acc, l) => {
        if (l.role?.toLowerCase() === 'user' && l.name) acc[l.name] = true;
        return acc;
    }, {})).length;
    
    const activeCitiesCount = new Set(
        logs.map(l => {
            const c = getLogCityName(l);
            return c.toLowerCase() === "all" ? "Todas as Cidades" : c;
        }).filter(Boolean)
    ).size;

    let mostActiveCity = "Nenhuma";
    let maxCityActions = 0;
    Object.values(cityClientsMap).forEach(c => {
        if (c.totalActions > maxCityActions) {
            maxCityActions = c.totalActions;
            mostActiveCity = c.cityName;
        }
    });

    // Uniform style for all inputs
    const inputStyle = {
        height: "40px",
        borderRadius: "6px",
        border: "1px solid #dee2e6",
        backgroundColor: "#fff",
        fontSize: "14px",
        paddingTop: "0",
        paddingBottom: "0"
    };

    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="fw-bold text-dark mb-1" style={{ fontSize: "24px" }}>Painel de Acessos e Cidades</h1>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>Monitore de forma clara o engajamento dos clientes e o trabalho interno</p>
                </div>
                <Button 
                    variant="light" 
                    className="d-flex align-items-center gap-2 px-3 py-2 border shadow-sm rounded-pill fw-semibold text-secondary"
                    onClick={() => navigate(`/inicio`)}
                    style={{ fontSize: "12px" }}
                >
                    <BsArrowLeft /> Voltar
                </Button>
            </div>

            {/* Filter controls */}
            <Card className="shadow-sm border-0 mb-4 bg-light">
                <Card.Body className="p-3">
                    <div className="row align-items-end g-3">
                        <div className="col-md-3 px-1">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-secondary mb-1" style={{ fontSize: "12px" }}>Período de Análise</Form.Label>
                                <Form.Select 
                                    value={periodType} 
                                    onChange={(e) => setPeriodType(e.target.value)}
                                    className="shadow-sm px-3 border"
                                    style={inputStyle}
                                >
                                    <option value="30days">Últimos 30 dias</option>
                                    <option value="thisMonth">Este mês</option>
                                    <option value="lastMonth">Mês passado</option>
                                    <option value="custom">Período personalizado</option>
                                </Form.Select>
                            </Form.Group>
                        </div>

                        {periodType === "custom" && (
                            <>
                                <div className="col-md-3 px-1">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-secondary mb-1" style={{ fontSize: "12px" }}>Data Início</Form.Label>
                                        <Form.Control 
                                            type="date" 
                                            value={customStart}
                                            onChange={(e) => setCustomStart(e.target.value)}
                                            className="shadow-sm border px-3"
                                            style={inputStyle}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3 px-1">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-secondary mb-1" style={{ fontSize: "12px" }}>Data Fim</Form.Label>
                                        <Form.Control 
                                            type="date" 
                                            value={customEnd}
                                            onChange={(e) => setCustomEnd(e.target.value)}
                                            className="shadow-sm border px-3"
                                            style={inputStyle}
                                        />
                                    </Form.Group>
                                </div>
                            </>
                        )}

                        <div className={`col-md-${periodType === "custom" ? "3" : "9"} px-1`}>
                            <Form.Group>
                                <Form.Label className="fw-semibold text-secondary mb-1" style={{ fontSize: "12px" }}>Filtrar Informações</Form.Label>
                                <div className="d-flex align-items-center shadow-sm rounded bg-white px-3 border" style={inputStyle}>
                                    <BsSearch className="text-muted me-2" size={14} />
                                    <Form.Control 
                                        type="text" 
                                        placeholder="Buscar por cidade, cliente ou colaborador..."
                                        value={searchText}
                                        onChange={handleSearchChange}
                                        className="border-0 bg-transparent p-0 w-100"
                                        style={{ height: "100%", boxShadow: "none", fontSize: "14px" }}
                                    />
                                </div>
                            </Form.Group>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* KPI Cards (Horizontal Layout) */}
            <div className="row g-3 mb-4">
                <div className="col-sm-6 col-lg-3 px-1">
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center p-3">
                            <div className="rounded bg-light text-primary me-2 d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", flexShrink: 0 }}>
                                <BsGraphUp size={18} />
                            </div>
                            <div className="text-start flex-grow-1 min-w-0">
                                <div className="fw-bold text-dark lh-1 mb-1" style={{ fontSize: "18px" }}>{totalActions}</div>
                                <div className="text-muted fw-normal text-truncate" style={{ fontSize: "11px" }}>Cliques Totais</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-sm-6 col-lg-3 px-1">
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center p-3">
                            <div className="rounded bg-light text-success me-2 d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", flexShrink: 0 }}>
                                <BsPeople size={18} />
                            </div>
                            <div className="text-start flex-grow-1 min-w-0">
                                <div className="fw-bold text-dark lh-1 mb-1" style={{ fontSize: "18px" }}>{activeClientsCount}</div>
                                <div className="text-muted fw-normal text-truncate" style={{ fontSize: "11px" }}>Clientes Ativos</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-sm-6 col-lg-3 px-1">
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center p-3">
                            <div className="rounded bg-light text-info me-2 d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", flexShrink: 0 }}>
                                <BsGeoAlt size={18} />
                            </div>
                            <div className="text-start flex-grow-1 min-w-0">
                                <div className="fw-bold text-dark lh-1 mb-1" style={{ fontSize: "18px" }}>{activeCitiesCount}</div>
                                <div className="text-muted fw-normal text-truncate" style={{ fontSize: "11px" }}>Cidades Monitoradas</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-sm-6 col-lg-3 px-1">
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center p-3">
                            <div className="rounded bg-light text-warning me-2 d-flex align-items-center justify-content-center" style={{ width: "42px", height: "42px", flexShrink: 0 }}>
                                <BsCalendar size={18} />
                            </div>
                            <div className="text-start flex-grow-1 min-w-0">
                                <div className="fw-bold text-dark lh-1 mb-1 text-truncate" style={{ fontSize: "16px" }} title={mostActiveCity}>{mostActiveCity}</div>
                                <div className="text-muted fw-normal text-truncate" style={{ fontSize: "11px" }}>Cidade mais Acessada</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Tabs Navigation */}
            <Tabs 
                activeKey={activeTab} 
                onSelect={(k) => setActiveTab(k)} 
                className="mb-4 custom-tabs border-bottom fw-bold"
                style={{ fontSize: "14px" }}
            >
                {/* TAB 1: CITIES AND CLIENT ACTIVITY */}
                <Tab eventKey="cities-clients" title="Clientes">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5 text-danger" style={{ fontSize: "14px" }}>Erro ao carregar dados do servidor.</div>
                    ) : filteredCities.length === 0 ? (
                        <div className="text-center py-5 text-muted" style={{ fontSize: "14px" }}>Nenhum acesso de cliente registrado no período selecionado.</div>
                    ) : (
                        <div className="row g-3 mt-1">
                            {filteredCities.map((cityGroup) => {
                                const clientsCount = Object.keys(cityGroup.clients).length;
                                return (
                                    <div className="col-md-6 col-lg-4 px-1" key={cityGroup.cityName}>
                                        <Card 
                                            className="h-100 border-0 shadow-sm card-hover border-top border-4 border-primary position-relative" 
                                            style={{ cursor: "pointer", transition: "transform 0.2s" }}
                                            onClick={() => setSelectedCityGroup(cityGroup)}
                                        >
                                            <Card.Body className="d-flex flex-column justify-content-between p-3">
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h4 className="fw-bold text-dark mb-0" style={{ fontSize: "16px" }}>{cityGroup.cityName}</h4>
                                                        <span className="badge bg-primary rounded-pill" style={{ fontSize: "11px" }}>
                                                            {cityGroup.totalActions} cliques
                                                        </span>
                                                    </div>
                                                    <p className="text-secondary mb-2" style={{ fontSize: "13px" }}>
                                                        <BsPeople className="me-1 text-primary" size={13} /> {clientsCount} {clientsCount === 1 ? 'cliente ativo' : 'clientes ativos'}
                                                    </p>
                                                </div>
                                                <div className="border-top pt-2 mt-2">
                                                    <div className="text-secondary fw-semibold" style={{ fontSize: "11px" }}>Última Atividade:</div>
                                                    <div className="text-dark fw-bold mt-1 text-truncate" style={{ fontSize: "12px" }} title={`${formatDate(cityGroup.lastAccessDate)} às ${cityGroup.lastAccessTime} por ${cityGroup.lastAccessUser}`}>
                                                        {formatDate(cityGroup.lastAccessDate)} às {cityGroup.lastAccessTime}
                                                        <span className="d-block text-secondary fw-normal mt-0.5" style={{ fontSize: "11px" }}>por {cityGroup.lastAccessUser}</span>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Tab>

                {/* TAB 2: STAFF WORK LOGS IN NATURAL LANGUAGE */}
                <Tab eventKey="staff" title="Colaboradores">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5 text-danger" style={{ fontSize: "14px" }}>Erro ao carregar dados do servidor.</div>
                    ) : filteredStaff.length === 0 ? (
                        <div className="text-center py-5 text-muted" style={{ fontSize: "14px" }}>Nenhuma atividade de colaboradores ou editores registrada no período.</div>
                    ) : (
                        <div>
                            {/* Expand/Collapse All Buttons */}
                            <div className="d-flex justify-content-end mb-3 gap-2">
                                <Button 
                                    variant="light" 
                                    size="sm" 
                                    onClick={() => setExpandedStaff({})}
                                    className="border shadow-sm rounded-pill fw-semibold text-secondary"
                                    style={{ fontSize: "11px", padding: "4px 12px" }}
                                >
                                    Recolher Todos
                                </Button>
                                <Button 
                                    variant="light" 
                                    size="sm" 
                                    onClick={() => {
                                        const all = {};
                                        filteredStaff.forEach(s => { all[s.name] = true; });
                                        setExpandedStaff(all);
                                    }}
                                    className="border shadow-sm rounded-pill fw-semibold text-secondary"
                                    style={{ fontSize: "11px", padding: "4px 12px" }}
                                >
                                    Expandir Todos
                                </Button>
                            </div>

                            <div className="row g-3 mt-1">
                                {filteredStaff.map((staff) => {
                                    const isExpanded = !!expandedStaff[staff.name];
                                    return (
                                        <div className="col-12 px-1" key={staff.name}>
                                            <Card 
                                                className="border-0 shadow-sm border-start border-4 border-warning card-hover"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => toggleStaff(staff.name)}
                                            >
                                                <Card.Body className="p-3">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <h5 className="fw-bold text-dark mb-0" style={{ fontSize: "15px" }}>{staff.name}</h5>
                                                                {isExpanded ? <BsChevronUp className="text-muted" size={12} /> : <BsChevronDown className="text-muted" size={12} />}
                                                            </div>
                                                            <span className={`badge rounded-pill mt-2 ${getRoleBadgeClass(staff.role)}`} style={{ fontSize: "10px" }}>
                                                                {staff.role}
                                                            </span>
                                                        </div>
                                                        <div className="text-end text-muted" style={{ fontSize: "12px" }}>
                                                            <div>Última atividade registrada em:</div>
                                                            <span className="fw-bold text-dark">{formatDate(staff.lastAccessDate)} às {staff.lastAccessTime}</span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Collapsible Natural Language Activity Summaries */}
                                                    {isExpanded && (
                                                        <div className="ps-2 border-top pt-3 mt-3">
                                                            <h6 className="fw-bold text-secondary mb-3 uppercase tracking-wide" style={{ fontSize: "11px" }}>Resumo de Atividades no Período:</h6>
                                                            {Object.values(staff.citiesWorked).map((cityStats) => (
                                                                <div key={cityStats.cityName} className="mb-2 d-flex align-items-center gap-2">
                                                                    <div className="bullet-dot bg-warning" style={{ width: "6px", height: "6px", flexShrink: 0 }}></div>
                                                                    <span className="text-dark" style={{ fontSize: "13px" }}>
                                                                        {getFriendlySummaryText(cityStats)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </Tab>
            </Tabs>

            {/* Hidden Audit Log Trigger */}
            <div className="d-flex justify-content-end border-top pt-3 mt-4">
                <Button 
                    variant="link" 
                    className="text-secondary text-decoration-none d-flex align-items-center gap-2 opacity-75 hover-opacity-100"
                    onClick={() => setShowTechLogs(true)}
                    style={{ fontSize: "12px" }}
                >
                    <BsCodeSlash /> Ver Logs Técnicos (Auditoria)
                </Button>
            </div>

            {/* MODAL 1: CITY CLIENT DETAIL SHEET */}
            <Modal 
                show={!!selectedCityGroup} 
                onHide={() => {
                    setSelectedCityGroup(null);
                    setSelectedClientDetail(null);
                }} 
                size="lg"
                centered
                className="custom-modal"
            >
                <Modal.Header closeButton className="border-0 bg-light px-4 py-3">
                    <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: "16px" }}>
                        <BsGeoAlt className="text-primary" /> Clientes Ativos em {selectedCityGroup?.cityName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    {selectedCityGroup && (
                        <div className="row g-3">
                            {/* Left Pane: Clients list */}
                            <div className="col-md-5 border-end px-1">
                                <h6 className="fw-bold text-secondary mb-3 uppercase tracking-wide" style={{ fontSize: "11px" }}>Selecione um Cliente</h6>
                                <div className="list-group rounded shadow-sm border-0">
                                    {Object.values(selectedCityGroup.clients).map(client => (
                                        <button 
                                            key={client.name}
                                            onClick={() => setSelectedClientDetail(client)}
                                            className={`list-group-item list-group-item-action border-0 px-3 py-2 d-flex justify-content-between align-items-center ${selectedClientDetail?.name === client.name ? 'active bg-primary' : ''}`}
                                            style={{ fontSize: "13px" }}
                                        >
                                            <span className="fw-semibold">{client.name}</span>
                                            <span className={`badge ${selectedClientDetail?.name === client.name ? 'bg-white text-primary' : 'bg-light text-secondary'} rounded-pill`} style={{ fontSize: "10px" }}>
                                                {client.totalActions} ações
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Right Pane: Timeline Journey */}
                            <div className="col-md-7 px-1">
                                {selectedClientDetail ? (
                                    <div>
                                        <h6 className="fw-bold text-secondary mb-4 uppercase tracking-wide" style={{ fontSize: "11px" }}>Linha do Tempo de Atividade ({selectedClientDetail.name})</h6>
                                        <div className="timeline-container ps-3">
                                            {selectedClientDetail.actions.map((act) => (
                                                <div key={act._id} className="timeline-item mb-3 position-relative border-start border-2 border-primary ps-3 pb-2">
                                                    <div className="timeline-badge bg-primary" style={{ width: "8px", height: "8px", left: "-5px" }}></div>
                                                    <div className="text-muted fw-semibold" style={{ fontSize: "11px" }}>{formatDate(act.date)} às {act.time}</div>
                                                    <div className="fw-bold text-dark mt-1" style={{ fontSize: "13px" }}>
                                                        {translateAction(act.method, act.path)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-5">
                                        <BsClockHistory size={24} className="mb-2 opacity-50" />
                                        <div style={{ fontSize: "12px" }}>Selecione um cliente para ver onde ele clicou ou navegou.</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* MODAL 2: RAW TECHNICAL AUDIT LOGS */}
            <Modal 
                show={showTechLogs} 
                onHide={() => setShowTechLogs(false)} 
                size="xl"
                centered
            >
                <Modal.Header closeButton className="border-0 bg-dark text-white px-4 py-3">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "15px" }}>
                        <BsCodeSlash /> Logs Técnicos e Auditoria Geral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: "75vh" }}>
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "11px" }}>
                            <thead className="table-dark text-secondary border-bottom sticky-top">
                                <tr>
                                    <th className="px-4 py-3 border-0">Usuário</th>
                                    <th className="py-3 border-0">Perfil</th>
                                    <th className="py-3 border-0">Cidade</th>
                                    <th className="py-3 border-0">Data / Hora</th>
                                    <th className="py-3 border-0 text-center">Método</th>
                                    <th className="py-3 border-0 px-4">Ação Traduzida</th>
                                    <th className="py-3 border-0 px-4">Caminho Técnico</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5 text-muted" style={{ fontSize: "13px" }}>
                                            Nenhum log encontrado para os critérios selecionados.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLogs.map(log => (
                                        <tr key={log._id}>
                                            <td className="px-4 py-3">
                                                <div className="fw-semibold text-dark">{log.name}</div>
                                            </td>
                                            <td>
                                                <span className={`badge rounded-pill ${getRoleBadgeClass(log.role)}`} style={{ fontSize: "9px" }}>
                                                    {log.role}
                                                </span>
                                            </td>
                                            <td className="fw-semibold text-secondary">
                                                {getLogCityName(log)}
                                            </td>
                                            <td className="text-muted">
                                                {formatDate(log.date)} {log.time}
                                            </td>
                                            <td className="text-center">
                                                <span className={`badge rounded px-2 py-1 ${getMethodBadgeClass(log.method)}`} style={{ fontSize: "9px" }}>
                                                    {log.method}
                                                </span>
                                            </td>
                                            <td className="text-dark fw-semibold">
                                                {translateAction(log.method, log.path)}
                                            </td>
                                            <td className="text-muted font-monospace" style={{ fontSize: "10px" }}>
                                                {log.path}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Custom CSS for timeline bullets and layouts */}
            <style dangerouslySetInnerHTML={{__html: `
                .timeline-badge {
                    position: absolute;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    left: -6px;
                    top: 5px;
                }
                .bullet-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    display: inline-block;
                }
                .x-small {
                    font-size: 11px;
                }
                .card-hover:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
                }
                .p-3\\.5 {
                    padding: 1.125rem !important;
                }
            `}} />
        </div>
    );
};

export default LogsPage;